import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceApiService, AudienceVolumeTrendPoint } from '../../../services/audience-api.service';
import { AudienceVolumesTrendChartComponent } from '../volumes-trend-chart/audience-volumes-trend-chart.component';

/**
 * Tiny per-activation push-volumes trend. Sits on each destination card next to "last export".
 * Fetches the last ~14 days of push volumes for one activation and renders them with the shared
 * bar chart in compact mode. Shows a muted dash while loading / on error / when there's nothing
 * to show.
 *
 * The parent gates it on `showAdvancedInsights` (admin_dashboard.all AND the `audienceInsights`
 * experimental flag), so the root carries `experimental-marker` like the other insights do.
 */
@Component({
  selector: 'app-audience-activation-push-chart',
  standalone: true,
  imports: [CommonModule, TranslateModule, AudienceVolumesTrendChartComponent],
  templateUrl: './audience-activation-push-chart.component.html',
  styleUrls: ['./audience-activation-push-chart.component.scss']
})
export class AudienceActivationPushChartComponent implements OnChanges {
  @Input() activationId: string | null = null;

  trend: AudienceVolumeTrendPoint[] = [];
  loading = false;
  error = false;

  private loadedFor: string | null = null;

  constructor(private audienceApi: AudienceApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activationId'] && this.activationId && this.activationId !== this.loadedFor) {
      this.load(this.activationId);
    }
  }

  private load(activationId: string): void {
    this.loadedFor = activationId;
    this.loading = true;
    this.error = false;
    this.trend = [];
    this.audienceApi.getActivationVolumesTrend(activationId).subscribe({
      next: (rows) => {
        if (this.loadedFor !== activationId) {
          return;
        }
        this.trend = rows;
        this.loading = false;
      },
      error: () => {
        if (this.loadedFor !== activationId) {
          return;
        }
        this.error = true;
        this.loading = false;
      }
    });
  }

  get hasData(): boolean {
    return this.trend.some(t => Number(t.Volumes) > 0);
  }
}
